/**
 * Reporting content — the in-app path Google Play requires.
 *
 * Open to signed-out users on purpose: Explore browses without an account and
 * the AI answers anonymous visitors, so the people most likely to meet bad
 * content are the ones who have not signed up.
 */
import { fetchApi } from './apiBase';

/** What a report can point at. Mirrors `plans/content_reports.TARGET_TYPES`. */
/**
 * Mirrors `plans/content_reports.TARGET_TYPES`. Only types the server both
 * accepts and can act on: shared itineraries and destination guides return
 * here when they have a control and a canonical identifier.
 */
export type ReportTargetType = 'community_plan' | 'ai_message';

export interface ReportOption {
  value: string;
  label: string;
}

export interface ReportOptions {
  target_types: ReportOption[];
  reasons: ReportOption[];
  max_detail_chars: number;
}

export interface SubmitReportInput {
  target_type: ReportTargetType;
  target_id: string;
  reason: string;
  detail?: string;
  /** Where the reporter was, for the owner's context. Never a target. */
  context?: string;
  /**
   * The reported content itself. Required for `ai_message`, whose id the
   * server cannot resolve to any row.
   */
  content_snapshot?: string;
}

/**
 * The reason vocabulary, fetched rather than hardcoded, so a client cannot
 * offer a reason the server will reject.
 */
export function getReportOptions(): Promise<ReportOptions> {
  return fetchApi<ReportOptions>('/api/plans/report/options/');
}

/**
 * Build the snapshot for an AI turn.
 *
 * The prose alone is not the response: a turn can render option lists, plan
 * changes and proposal cards, and two turns with identical prose but different
 * components are different content. Folding them onto one hash would merge
 * unrelated reports and hand the operator only half of what was on screen.
 *
 * Two details that look fussy and are not:
 *
 * - Slicing is by CODE POINT, not by `String.length`. JavaScript counts UTF-16
 *   units, so a boundary landing inside an emoji leaves a lone surrogate, and
 *   the server cannot encode that — the reporter would get a failure they can
 *   never retry past. Code points are also what Python's `len()` counts, so
 *   this cap and the server's are the same cap.
 * - The extras get their own budget rather than being appended afterwards. With
 *   append-then-truncate, two responses sharing 2000 characters of prose and
 *   differing only in their components produced byte-identical snapshots
 *   containing neither component, which is exactly the collision the extras
 *   exist to prevent.
 */
const EXTRAS_BUDGET = 600;

function sliceByCodePoint(text: string, max: number): string {
  if (max <= 0) return '';
  const points = Array.from(text);
  if (points.length <= max) return text;
  return `${points.slice(0, max - 1).join('')}\u2026`;
}

export function buildAiSnapshot(
  content: string,
  extras?: unknown,
  maxChars = 2000,
): string {
  let extrasText = '';
  if (extras !== undefined && extras !== null) {
    try {
      const serialised = JSON.stringify(extras);
      if (serialised && serialised !== '[]' && serialised !== '{}' && serialised !== 'null') {
        extrasText = `\n\n[shown with this response]\n${serialised}`;
      }
    } catch {
      // A component that cannot be serialised is not worth losing the report
      // over; the prose still goes.
    }
  }
  extrasText = sliceByCodePoint(extrasText, Math.min(EXTRAS_BUDGET, maxChars));
  const proseBudget = maxChars - Array.from(extrasText).length;
  return sliceByCodePoint(content ?? '', proseBudget) + extrasText;
}

export function submitReport(input: SubmitReportInput): Promise<{ status: string }> {
  return fetchApi<{ status: string }>('/api/plans/report/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
